/**
 * Login Screen
 * Accepts email or mobile number + password (password auth)
 * OR country code + mobile number + OTP (OTP auth)
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, ScrollView, StyleSheet, ActivityIndicator, Alert, Image, TouchableOpacity, Linking, TextInput } from 'react-native';
import { useAuthStore, validateCaptcha, getConfig } from '@app/core';
import { Button, Input, Card, Text, TurnstileCaptcha } from '@app/design-system';
import { colors, sizes } from '@app/design-system';

// Common country codes for OTP
const COUNTRY_CODES = [
  { code: '91', label: 'IN (+91)', flag: '🇮🇳' },
  { code: '1', label: 'US (+1)', flag: '🇺🇸' },
  { code: '44', label: 'UK (+44)', flag: '🇬🇧' },
  { code: '971', label: 'UAE (+971)', flag: '🇦🇪' },
  { code: '65', label: 'SG (+65)', flag: '🇸🇬' },
  { code: '61', label: 'AU (+61)', flag: '🇦🇺' },
  { code: '49', label: 'DE (+49)', flag: '🇩🇪' },
  { code: '33', label: 'FR (+33)', flag: '🇫🇷' },
];

const OTP_RESEND_SECONDS = 60;

type LoginMode = 'password' | 'otp';
type OtpStep = 'mobile' | 'otp';

interface LoginScreenProps {
  onLoginSuccess?: () => void;
  onNavigateToSignup?: () => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({
  onLoginSuccess,
  onNavigateToSignup,
}) => {
  // ---- SHARED STATE ----
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [validatingCaptcha, setValidatingCaptcha] = useState(false);
  const [captchaValidated, setCaptchaValidated] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(true);
  const [logoError, setLogoError] = useState(false);

  // ---- PASSWORD MODE STATE ----
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<{ userId?: string; password?: string }>({});
  const [isPasswordFormValid, setIsPasswordFormValid] = useState(false);

  // ---- OTP MODE STATE ----
  const [otpStep, setOtpStep] = useState<OtpStep>('mobile');
  const [selectedCountryCode, setSelectedCountryCode] = useState('91');
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpErrors, setOtpErrors] = useState<{ mobile?: string; otp?: string }>({});
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const resendTimerRef = useRef<any>(null);

  const logoUrl = '/assets/icons/Bazly_Logo.jpg';
  const { login, loginWithOtp, generateOtp, isLoading, error } = useAuthStore();

  // ---- DYNAMIC VALIDATION: PASSWORD MODE ----
  useEffect(() => {
    const isUserIdValid = userId.trim() && isValidUserId(userId);
    const isPasswordValid = password.trim() && password.length >= 6;
    setIsPasswordFormValid(!!(isUserIdValid && isPasswordValid));
  }, [userId, password]);

  // Clean up resend timer on unmount
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const startResendTimer = () => {
    setResendTimer(OTP_RESEND_SECONDS);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const isValidUserId = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^\d{10,12}$/;
    return emailRegex.test(value) || mobileRegex.test(value);
  };

  const isValidMobile = (value: string): boolean => /^\d{7,12}$/.test(value.trim());

  // ---- CAPTCHA HANDLERS ----
  const handleCaptchaVerify = async (token: string) => {
    console.log('🔐 [LoginScreen] Captcha token received');
    try {
      setValidatingCaptcha(true);
      setCaptchaToken(token);
      const response = await validateCaptcha({ token });
      if (response.success) {
        setCaptchaValidated(true);
        setTimeout(() => setShowCaptcha(false), 500);
      } else {
        setCaptchaValidated(false);
        Alert.alert('Verification Failed', response.message || 'Captcha validation failed. Please try again.', [{ text: 'Try Again', onPress: () => { setCaptchaToken(null); setCaptchaValidated(false); } }]);
      }
    } catch (error: any) {
      setCaptchaValidated(false);
      Alert.alert('Verification Error', error.message || 'Failed to validate captcha.', [{ text: 'Try Again', onPress: () => { setCaptchaToken(null); setCaptchaValidated(false); } }]);
    } finally {
      setValidatingCaptcha(false);
    }
  };

  // ---- PASSWORD LOGIN ----
  const validatePasswordForm = (): boolean => {
    const newErrors: { userId?: string; password?: string } = {};
    if (!userId.trim()) {
      newErrors.userId = 'Email or mobile number is required';
    } else if (!isValidUserId(userId)) {
      newErrors.userId = 'Please enter a valid email or mobile number';
    }
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordLogin = async (): Promise<void> => {
    if (!validatePasswordForm()) return;
    if (!captchaValidated) {
      Alert.alert('Verification Required', 'Please complete the captcha verification before logging in.');
      return;
    }
    try {
      console.log('🚀 [LoginScreen] Starting password login...');
      await login(userId, password);
      console.log('✅ [LoginScreen] Password login successful');
      if (onLoginSuccess) onLoginSuccess();
    } catch (err) {
      console.error('❌ [LoginScreen] Password login failed:', err);
    }
  };

  // ---- OTP LOGIN FLOW ----
  const validateMobileForm = (): boolean => {
    const newErrors: { mobile?: string } = {};
    if (!mobileNumber.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!isValidMobile(mobileNumber)) {
      newErrors.mobile = 'Please enter a valid mobile number (7-12 digits)';
    }
    setOtpErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async (): Promise<void> => {
    if (!validateMobileForm()) return;
    if (!captchaValidated) {
      Alert.alert('Verification Required', 'Please complete the captcha verification first.');
      return;
    }
    setSendingOtp(true);
    setOtpErrors({});
    try {
      console.log('📱 [LoginScreen] Sending OTP to:', selectedCountryCode, mobileNumber);
      const result = await generateOtp(selectedCountryCode, mobileNumber);
      if (result.success) {
        console.log('✅ [LoginScreen] OTP sent successfully');
        setOtpSent(true);
        setOtpStep('otp');
        startResendTimer();
        // Show test hint in non-production
        const isNonProd = typeof window !== 'undefined' &&
          (window.location.hostname.includes('localhost') || window.location.hostname.includes('uat'));
        if (isNonProd) {
          Alert.alert('OTP Sent 📲', `${result.message}\n\n🧪 Test Environment: Use OTP "123456"`);
        } else {
          Alert.alert('OTP Sent 📲', result.message);
        }
      } else {
        console.error('❌ [LoginScreen] OTP send failed:', result.message);
        setOtpErrors({ mobile: result.message });
      }
    } catch (err: any) {
      console.error('❌ [LoginScreen] OTP generation error:', err);
      setOtpErrors({ mobile: err.message || 'Failed to send OTP. Please try again.' });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResendOtp = async (): Promise<void> => {
    if (resendTimer > 0) return;
    setOtp('');
    await handleSendOtp();
  };

  const handleOtpLogin = async (): Promise<void> => {
    if (!otp.trim() || otp.length < 4) {
      setOtpErrors({ otp: 'Please enter the OTP received on your mobile' });
      return;
    }
    if (!captchaValidated) {
      Alert.alert('Verification Required', 'Please complete the captcha verification first.');
      return;
    }
    try {
      console.log('🚀 [LoginScreen] Starting OTP login...');
      await loginWithOtp(selectedCountryCode, mobileNumber, otp);
      console.log('✅ [LoginScreen] OTP login successful');
      if (onLoginSuccess) onLoginSuccess();
    } catch (err: any) {
      console.error('❌ [LoginScreen] OTP login failed:', err);
      setOtpErrors({ otp: err.message || 'Invalid or expired OTP. Please try again.' });
    }
  };

  const handleBackToMobile = () => {
    setOtpStep('mobile');
    setOtp('');
    setOtpErrors({});
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
  };

  const handleSwitchMode = (mode: LoginMode) => {
    setLoginMode(mode);
    setPasswordErrors({});
    setOtpErrors({});
    setOtpStep('mobile');
    setOtpSent(false);
    setOtp('');
  };

  const handleLogoClick = () => {
    Linking.openURL('https://bazly.in');
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.code === selectedCountryCode) || COUNTRY_CODES[0];

  return (
    <View style={styles.darkBackground}>
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.modalCard}>
          {/* Logo Section */}
          <TouchableOpacity onPress={handleLogoClick} style={styles.logoContainer} activeOpacity={0.8}>
            {!logoError ? (
              <Image
                source={{ uri: logoUrl }}
                style={styles.logo}
                resizeMode="contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <View style={styles.logoFallback}>
                <Text variant="h1" bold style={styles.logoFallbackText}>Bazly</Text>
              </View>
            )}
          </TouchableOpacity>

          <View style={styles.headerSection}>
            <Text variant="h1" bold style={styles.title}>Welcome Back</Text>
            <Text variant="body" style={styles.subtitle}>
              {!captchaValidated ? 'Verifying security...' : 'Sign in to your account'}
            </Text>
          </View>

          {/* Login Mode Toggle */}
          {captchaValidated && (
            <View style={styles.modeToggleContainer}>
              <TouchableOpacity
                style={[styles.modeTab, loginMode === 'password' && styles.modeTabActive]}
                onPress={() => handleSwitchMode('password')}
              >
                <Text style={[styles.modeTabText, loginMode === 'password' && styles.modeTabTextActive]}>
                  🔑 Password
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modeTab, loginMode === 'otp' && styles.modeTabActive]}
                onPress={() => handleSwitchMode('otp')}
              >
                <Text style={[styles.modeTabText, loginMode === 'otp' && styles.modeTabTextActive]}>
                  📱 OTP Login
                </Text>
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.formSection}>
            {/* Captcha */}
            {showCaptcha && !captchaValidated && (
              <View style={styles.pageLoadCaptchaContainer}>
                <Text style={styles.captchaLabel}>
                  {validatingCaptcha ? '🔐 Validating with server...' : '🔐 Verifying security...'}
                </Text>
                {validatingCaptcha && (
                  <View style={styles.validatingRow}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.validatingText}>Validating captcha...</Text>
                  </View>
                )}
                <TurnstileCaptcha
                  siteKey={getConfig().turnstileSiteKey}
                  onVerify={handleCaptchaVerify}
                  onError={() => {
                    Alert.alert('Verification Error', 'Security verification failed. Please refresh the page.', [{ text: 'Refresh', onPress: () => (window as any).location.reload() }]);
                  }}
                  theme="auto"
                  size="flexible"
                />
                <Text style={styles.captchaHelpText}>This may take a few seconds to complete automatically</Text>
              </View>
            )}

            {/* PASSWORD LOGIN FORM */}
            {captchaValidated && loginMode === 'password' && (
              <>
                <Input
                  label="Email or Mobile Number"
                  placeholder="example@shop.com or 919876543210"
                  value={userId}
                  onChangeText={setUserId}
                  keyboardType="email-address"
                  error={passwordErrors.userId}
                  testID="login-userid-input"
                />
                <Input
                  label="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry
                  error={passwordErrors.password}
                  testID="login-password-input"
                />
                {error && (
                  <View style={styles.errorContainer}>
                    <Text variant="caption" color="error">{error}</Text>
                  </View>
                )}
                <Button
                  onPress={handlePasswordLogin}
                  disabled={isLoading || !isPasswordFormValid || !captchaValidated}
                  loading={isLoading}
                  style={[styles.loginButton, (!isPasswordFormValid || !captchaValidated) && styles.loginButtonDisabled]}
                  testID="login-button"
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </>
            )}

            {/* OTP LOGIN FORM — Step 1: Mobile */}
            {captchaValidated && loginMode === 'otp' && otpStep === 'mobile' && (
              <>
                <Text style={styles.otpHelpText}>
                  Enter your registered mobile number to receive a one-time password.
                </Text>

                {/* Country Code + Mobile Number Row */}
                <View style={styles.mobileRow}>
                  {/* Country Code Picker */}
                  <View style={styles.countryCodeWrapper}>
                    <Text style={styles.inputLabel}>Country</Text>
                    <TouchableOpacity
                      style={styles.countryCodeButton}
                      onPress={() => setShowCountryPicker(!showCountryPicker)}
                    >
                      <Text style={styles.countryCodeText}>
                        {selectedCountry.flag} +{selectedCountry.code}
                      </Text>
                      <Text style={styles.dropdownArrow}>▼</Text>
                    </TouchableOpacity>
                    {showCountryPicker && (
                      <View style={styles.countryDropdown}>
                        {COUNTRY_CODES.map((c) => (
                          <TouchableOpacity
                            key={c.code}
                            style={[styles.countryDropdownItem, selectedCountryCode === c.code && styles.countryDropdownItemSelected]}
                            onPress={() => {
                              setSelectedCountryCode(c.code);
                              setShowCountryPicker(false);
                            }}
                          >
                            <Text style={styles.countryDropdownText}>{c.flag} {c.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}
                  </View>

                  {/* Mobile Number */}
                  <View style={styles.mobileInputWrapper}>
                    <Input
                      label="Mobile Number"
                      placeholder="9876543210"
                      value={mobileNumber}
                      onChangeText={(v) => setMobileNumber(v.replace(/\D/g, ''))}
                      keyboardType="phone-pad"
                      error={otpErrors.mobile}
                      testID="otp-mobile-input"
                    />
                  </View>
                </View>

                {error && (
                  <View style={styles.errorContainer}>
                    <Text variant="caption" color="error">{error}</Text>
                  </View>
                )}

                <Button
                  onPress={handleSendOtp}
                  disabled={sendingOtp || !mobileNumber.trim()}
                  loading={sendingOtp}
                  style={styles.loginButton}
                  testID="send-otp-button"
                >
                  {sendingOtp ? 'Sending OTP...' : 'Send OTP'}
                </Button>
              </>
            )}

            {/* OTP LOGIN FORM — Step 2: Enter OTP */}
            {captchaValidated && loginMode === 'otp' && otpStep === 'otp' && (
              <>
                <View style={styles.otpSentBanner}>
                  <Text style={styles.otpSentIcon}>📲</Text>
                  <View style={styles.otpSentTextWrapper}>
                    <Text style={styles.otpSentTitle}>OTP Sent Successfully</Text>
                    <Text style={styles.otpSentSubtitle}>
                      A 6-digit OTP was sent to +{selectedCountryCode} {mobileNumber}
                    </Text>
                  </View>
                </View>

                <Input
                  label="Enter OTP"
                  placeholder="• • • • • •"
                  value={otp}
                  onChangeText={(v) => {
                    setOtp(v.replace(/\D/g, '').slice(0, 6));
                    if (otpErrors.otp) setOtpErrors({});
                  }}
                  keyboardType="number-pad"
                  error={otpErrors.otp}
                  testID="otp-input"
                />

                {/* Resend + Back Row */}
                <View style={styles.otpActionsRow}>
                  <TouchableOpacity onPress={handleBackToMobile} style={styles.otpBackButton}>
                    <Text style={styles.otpBackText}>← Change Number</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleResendOtp}
                    disabled={resendTimer > 0 || sendingOtp}
                    style={[styles.resendButton, resendTimer > 0 && styles.resendButtonDisabled]}
                  >
                    {sendingOtp ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Text style={[styles.resendText, resendTimer > 0 && styles.resendTextDisabled]}>
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>

                {error && (
                  <View style={styles.errorContainer}>
                    <Text variant="caption" color="error">{error}</Text>
                  </View>
                )}

                <Button
                  onPress={handleOtpLogin}
                  disabled={isLoading || otp.length < 4}
                  loading={isLoading}
                  style={[styles.loginButton, otp.length < 4 && styles.loginButtonDisabled]}
                  testID="verify-otp-button"
                >
                  {isLoading ? 'Verifying OTP...' : 'Verify & Login'}
                </Button>
              </>
            )}
          </View>

          <View style={styles.signupSection}>
            <Text variant="body" style={styles.signupText}>
              Don't have a shop account?
            </Text>
            <Button variant="ghost" onPress={onNavigateToSignup} testID="navigate-to-signup">
              Create a Shop
            </Button>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  darkBackground: {
    flex: 1,
    backgroundColor: '#1a1a1a',
    minHeight: '100vh' as any,
  },
  container: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  modalCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    paddingHorizontal: 32,
    paddingVertical: 40,
    width: '100%',
    maxWidth: 480,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' as any,
    elevation: 8,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    width: 180,
    height: 60,
  },
  logoFallback: {
    width: 120,
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
  },
  logoFallbackText: {
    fontSize: 24,
    color: '#333',
  },
  headerSection: {
    marginBottom: 24,
    alignItems: 'center',
  },
  title: {
    marginBottom: 8,
    color: '#1a1a1a',
    fontSize: 28,
  },
  subtitle: {
    fontSize: 14,
    color: colors.gray7,
    textAlign: 'center',
  },
  // Mode toggle tabs
  modeToggleContainer: {
    flexDirection: 'row',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 24,
    overflow: 'hidden',
  },
  modeTab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  modeTabActive: {
    backgroundColor: colors.primary,
  },
  modeTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.gray7,
  },
  modeTabTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  formSection: {
    marginBottom: 24,
  },
  pageLoadCaptchaContainer: {
    padding: 24,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    marginBottom: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  captchaLabel: {
    fontSize: 13,
    color: colors.gray8,
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: '500',
  },
  captchaHelpText: {
    fontSize: 12,
    color: colors.gray6,
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  validatingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    gap: 8,
  },
  validatingText: {
    fontSize: 12,
    color: colors.gray7,
  },
  errorContainer: {
    backgroundColor: colors.errorLight,
    padding: 12,
    borderRadius: 8,
    marginVertical: 12,
  },
  loginButton: {
    marginTop: 16,
  },
  loginButtonDisabled: {
    opacity: 0.6,
  },
  signupSection: {
    alignItems: 'center',
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  signupText: {
    marginBottom: 8,
    color: colors.gray7,
  },
  // OTP specific styles
  otpHelpText: {
    fontSize: 13,
    color: colors.gray7,
    marginBottom: 16,
    lineHeight: 20,
  },
  mobileRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
  },
  countryCodeWrapper: {
    width: 110,
    position: 'relative',
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  countryCodeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 12,
    backgroundColor: '#f9fafb',
    height: 48,
  },
  countryCodeText: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '500',
  },
  dropdownArrow: {
    fontSize: 10,
    color: colors.gray6,
  },
  countryDropdown: {
    position: 'absolute',
    top: 72,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)' as any,
    elevation: 8,
    maxHeight: 220,
  },
  countryDropdownItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  countryDropdownItemSelected: {
    backgroundColor: '#eff6ff',
  },
  countryDropdownText: {
    fontSize: 13,
    color: '#374151',
  },
  mobileInputWrapper: {
    flex: 1,
  },
  otpSentBanner: {
    flexDirection: 'row',
    backgroundColor: '#f0fdf4',
    borderRadius: 10,
    padding: 14,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#bbf7d0',
    gap: 12,
    alignItems: 'flex-start',
  },
  otpSentIcon: {
    fontSize: 24,
    marginTop: 2,
  },
  otpSentTextWrapper: {
    flex: 1,
  },
  otpSentTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#166534',
    marginBottom: 3,
  },
  otpSentSubtitle: {
    fontSize: 13,
    color: '#15803d',
    lineHeight: 18,
  },
  otpActionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 4,
  },
  otpBackButton: {
    paddingVertical: 6,
  },
  otpBackText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '500',
  },
  resendButton: {
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  resendButtonDisabled: {
    opacity: 0.5,
  },
  resendText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '500',
  },
  resendTextDisabled: {
    color: colors.gray6,
  },
});

